/* eslint-disable react/display-name */
import Button from 'components/Button/Button';
import moment from 'moment';
import React, { ReactNode, useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { FulfillmentNote } from 'types/schema';
import messages from '../OrdersPage/messages';
import SVGIcon from 'components/SVGIcon/SVGIcon';
import downloadIcon from './arrowDownloadIcon.svg';

const Header = styled.h1`
  color: #343538;
  font-weight: 600;
  font-size: 24px;
  line-height: 24px;
  margin: 0;
`;

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin: 37px 0 20px;

  @media (max-width: 767px) {
    padding: 0 10pt;
    margin: 15px 0 20px;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const ButtonText = styled.span`
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #343538;
`;

const Icon = styled(SVGIcon)`
  width: 20px;
  height: 20px;
`;

const ActionButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 16px;
  min-width: 100px;
`;

const RateButton = styled(ActionButton)`
  background-color: #43a047;

  &:hover,
  &:focus,
  &:active {
    background-color: #43a047 !important;
  }
`;

const RateButtonText = styled(ButtonText)`
  color: #fff;
`;

interface FulfillmentNoteDetailPageHeaderProps {
  onBulkReOrder: (data: { storeId: string; orderIds: string[] }) => void;
  onDownloadDeliveryNote: (fulfillmentNoteId: string, cb: () => void) => void;
  asComponent?: any;
  semibold?: boolean;
  children?: ReactNode;
  fulfillmentNote: FulfillmentNote;
  store: string;
  onOpenModal: (note: FulfillmentNote) => void;
}

export default function (props: FulfillmentNoteDetailPageHeaderProps) {
  const {
    asComponent,
    semibold,
    fulfillmentNote,
    store,
    onBulkReOrder,
    onOpenModal,
    onDownloadDeliveryNote,
    ...delegated
  } = props;

  const [downloading, setDownloading] = useState(false);

  const shouldShowRateButton =
    fulfillmentNote.firstOrder?.status === 'DELIVERED' &&
    moment().diff(moment(fulfillmentNote.orderDateTime), 'days') <= 7 &&
    !fulfillmentNote.feedback?.rating;

  const downloadDeliveryNote = useCallback(() => {
    setDownloading(true);
    onDownloadDeliveryNote(fulfillmentNote?.fulfillmentNoteId, () => {
      setDownloading(false);
    });
  }, [onDownloadDeliveryNote, fulfillmentNote?.fulfillmentNoteId]);

  return (
    <Container {...delegated}>
      <Header semibold={semibold} as={asComponent}>
        {props.children}
      </Header>

      <ButtonWrapper>
        {(fulfillmentNote?.fulfillmentNoteStatus === 'DELIVERY_IN_PROGRESS' ||
          fulfillmentNote?.fulfillmentNoteStatus === 'DELIVERED') && (
          <ActionButton onClick={downloadDeliveryNote} disabled={downloading} loading={downloading}>
            <Icon src={downloadIcon} />
            <ButtonText>
              <FormattedMessage {...messages.downloadDeliveryNote} />
            </ButtonText>
          </ActionButton>
        )}

        <ActionButton onClick={() => onBulkReOrder({ orderIds: fulfillmentNote.orderIds, storeId: store })}>
          <ButtonText>
            <FormattedMessage {...messages.reOrder} />
          </ButtonText>
        </ActionButton>

        {shouldShowRateButton ? (
          <RateButton onClick={() => onOpenModal(fulfillmentNote)}>
            <RateButtonText>
              <FormattedMessage {...messages.rate} />
            </RateButtonText>
          </RateButton>
        ) : null}
      </ButtonWrapper>
    </Container>
  );
}
